import { IconLoader2, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { DiscountListItemType } from "@/features/cart/types/discount.type";
import { DiscountMobileApp } from "@/features/discounts/components/discount-mobile-app";
import { isMobileDevice } from "@/lib/helpers/is-mobile-device";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";
import type { MobileAppDataType } from "@/types/mobile-app-data.type";

type AddDiscountCouponButtonProps = {
  onRemove?: () => void;
  onUse?: (onlyMobileApp: boolean) => void;
  isActive?: boolean;
  isDisabled?: boolean;

  isLoading?: boolean;
  discount: DiscountListItemType;
  mobileAppData?: MobileAppDataType | null;
};

export const AddDiscountCouponButton = ({
  discount,
  onRemove,
  onUse,
  isDisabled = false,
  isActive = false,

  isLoading = false,
  mobileAppData,
}: AddDiscountCouponButtonProps) => {
  const t = useTranslations();
  // This is used to show the loading state when the specific button is clicked
  const [isClickLoading, setIsClickLoading] = useState(false);
  const [showAppButton, setShowAppButton] = useState(false);

  const handleClick = () => {
    if (discount.onlyMobileApp && !isMobileDevice()) {
      setShowAppButton(true);
    }

    if (isActive) {
      setIsClickLoading(true);
      onRemove?.();
    } else {
      setIsClickLoading(true);
      onUse?.(discount.onlyMobileApp);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      setIsClickLoading(false);
    }
  }, [isLoading]);

  const getLabel = (): React.ReactNode => {
    if (discount.onlyMobileApp) {
      return t("discounts.goToApp");
    }
    if (isDisabled) {
      return t("discounts.labelDisabled");
    }
    if (isActive) {
      return (
        <>
          {t("discounts.labelActive")} <IconX className="h-6 w-6" />
        </>
      );
    }
    return t("discounts.labelDefault");
  };

  return (
    <div className="relative grid min-h-[52px] overflow-hidden">
      {mobileAppData && (
        <DiscountMobileApp
          mobileAppData={mobileAppData}
          showAppButton={showAppButton}
          strapiUrl={strapiUrl}
        />
      )}

      <Button
        className={cn(
          "col-start-1 row-start-1 flex w-full items-center justify-center gap-2 border-brand-green2-300 text-medium transition-transform duration-300 ease-out",
          showAppButton ? "-translate-y-full" : "translate-y-0"
        )}
        disabled={isDisabled}
        onClick={handleClick}
        variant="outline"
      >
        {getLabel()}{" "}
        {isLoading && isClickLoading ? (
          <IconLoader2 className="h-4 w-4 animate-spin" />
        ) : null}
      </Button>
    </div>
  );
};

